import type { Paragraph, TextLine, TextRun } from '@/api/types/stowed-element'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { restateLeadingPresetForPointSize } from '@/lib/text/leading-presets'
import { extractRunStyle, runStyleEquals } from '@/lib/text/run-style'
import type { ParagraphIndexRange } from './types/paragraph-index-range'

export function cloneParagraphsDeep(paragraphs: Paragraph[]): Paragraph[] {
  try {
    return JSON.parse(JSON.stringify(paragraphs)) as Paragraph[]
  } catch {
    return paragraphs
  }
}

export function mergeTextRunStyle(run: TextRun, u: TextRunStyleUpdate): TextRun {
  const next = { ...run }

  if (u.fontFamilyId !== undefined) next.fontFamilyId = u.fontFamilyId
  if (u.fontId !== undefined) next.fontId = u.fontId
  if (u.fontIdByFontId !== undefined && run.fontId !== undefined) {
    const mapped = u.fontIdByFontId[run.fontId]

    if (mapped !== undefined) next.fontId = mapped
  }

  if (u.pointSize !== undefined) next.pointSize = u.pointSize
  if (u.underline !== undefined) next.underline = u.underline
  if (u.strikethrough !== undefined) next.strikethrough = u.strikethrough
  if (u.kerning !== undefined) next.kerning = u.kerning
  if (u.tracking !== undefined) next.tracking = u.tracking
  if (u.leading !== undefined) next.leading = u.leading

  // A point-size change moves a Narrow/Wide preset leading with it (so the preset still matches the
  // new size and rows don't overlap — WMPCM-9070 Bug 6), but leaves a value the user typed fixed in
  // points (AC6, WMPCM-9899) — see `restateLeadingPresetForPointSize`. Skipped when the update sets
  // `leading` itself: the user is choosing both, so their explicit value wins. This is the one place
  // a run's `pointSize` changes — every run-level apply path (`applyStyleUpdateToParagraphs`,
  // `applyUpdateToLineRange`, and the shape-text range appliers that delegate to it) funnels here.
  if (u.pointSize !== undefined && u.leading === undefined) {
    const restated = restateLeadingPresetForPointSize(next.leading, run.pointSize, u.pointSize)

    if (restated !== undefined) next.leading = restated
  }

  return next
}

/** Run-level fields of an update (excludes paragraph alignment) for pending/typing attributes. */
export function pickRunStyleFields(u: TextRunStyleUpdate): TextRunStyleUpdate {
  const out: TextRunStyleUpdate = {}

  if (u.fontFamilyId !== undefined) out.fontFamilyId = u.fontFamilyId
  if (u.fontId !== undefined) out.fontId = u.fontId
  if (u.pointSize !== undefined) out.pointSize = u.pointSize
  if (u.underline !== undefined) out.underline = u.underline
  if (u.strikethrough !== undefined) out.strikethrough = u.strikethrough
  if (u.kerning !== undefined) out.kerning = u.kerning
  if (u.tracking !== undefined) out.tracking = u.tracking
  if (u.leading !== undefined) out.leading = u.leading

  return out
}

export function mergeAdjacentRuns(runs: TextRun[]): TextRun[] {
  const out: TextRun[] = []

  for (const run of runs) {
    const prev = out[out.length - 1]

    if (prev && runStyleEquals(extractRunStyle(prev), extractRunStyle(run))) {
      prev.textContent = (prev.textContent ?? '') + (run.textContent ?? '')
    } else {
      out.push({ ...run })
    }
  }

  return out
}

/** Apply a run-style update to the `[start, end)` character range of a single line, splitting runs. */
export function applyUpdateToLineRange(
  line: TextLine,
  start: number,
  end: number,
  u: TextRunStyleUpdate
): TextLine {
  const runs = line.textRuns ?? []
  const out: TextRun[] = []
  let pos = 0

  for (const run of runs) {
    const text = run.textContent ?? ''
    const len = text.length
    const runStart = pos
    const runEnd = pos + len

    pos = runEnd

    if (len === 0) {
      out.push(run)
      continue
    }

    const overlapStart = Math.max(start, runStart)
    const overlapEnd = Math.min(end, runEnd)

    if (overlapStart >= overlapEnd) {
      out.push(run)
      continue
    }

    const beforeText = text.slice(0, overlapStart - runStart)
    const midText = text.slice(overlapStart - runStart, overlapEnd - runStart)
    const afterText = text.slice(overlapEnd - runStart)

    if (beforeText.length > 0) out.push({ ...run, textContent: beforeText })
    out.push(mergeTextRunStyle({ ...run, textContent: midText }, u))
    if (afterText.length > 0) out.push({ ...run, textContent: afterText })
  }

  return { ...line, textRuns: mergeAdjacentRuns(out) }
}

/**
 * Apply a style update to paragraphs. Alignment is scoped to `paragraphIndexRange`
 * when provided; run-level styles always apply to every paragraph.
 */
export function applyStyleUpdateToParagraphs(
  paragraphs: Paragraph[],
  u: TextRunStyleUpdate,
  paragraphIndexRange?: ParagraphIndexRange | null
): Paragraph[] {
  if (paragraphs.length === 0) return paragraphs

  return paragraphs.map((p, idx) => {
    const nextP: Paragraph = { ...p }
    const alignmentInRange =
      paragraphIndexRange == null ||
      (idx >= paragraphIndexRange.start && idx <= paragraphIndexRange.end)

    if (u.alignment !== undefined && alignmentInRange) {
      nextP.alignment = u.alignment
    }

    // Paragraph-level, same as alignment above.
    if (u.dropCap !== undefined && alignmentInRange) nextP.dropCap = u.dropCap
    if (u.dropCapLines !== undefined && alignmentInRange) nextP.dropCapLines = u.dropCapLines
    if (u.dropCapOffset !== undefined && alignmentInRange) nextP.dropCapOffset = u.dropCapOffset

    nextP.textLines = (p.textLines ?? []).map((line) => ({
      ...line,
      textRuns: (line.textRuns ?? []).map((run) => mergeTextRunStyle(run, u)),
    }))

    return nextP
  })
}
